/**
 * SUPERBA CARD SWATCHES — cover strip on collection product cards
 * ---------------------------------------------------------------------------
 * Adds the search dropdown's swatch strip to every <product-card> on the page.
 * Hover a swatch to preview that colourway, click it to go straight there.
 *
 * Needs nothing from Liquid: <product-card> already carries handle="…"
 * (snippets/product-card.liquid line 48), which is all the lookup requires.
 *
 * Reuses assets/search-index.json — the same file the header search loads, so
 * on a second page view it is already in the browser cache. Swatch images come
 * Swatch photos are cached under ss_cw_* — deliberately NOT the search's ss_sw_*
 * keys, because this script stores no product photo and would otherwise wipe the
 * one the search dropdown's hover preview depends on.
 *
 * Regenerate the index with: node tools/build-index.js
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-superba-card-swatches]');
  if (!root) return;

  var INDEX_URL = root.dataset.indexUrl;
  var SHOP = root.dataset.shopDomain;
  var TOKEN = root.dataset.storefrontToken;
  /* Three on narrow cards, so each stays big enough to hit with a thumb;
     the rest roll into the +n. */
  var NARROW = window.matchMedia('(max-width: 999px)');

  var byHandle = {};       /* product handle -> its piece row */
  var swatchCache = {};

  /* ------------------------------------------------------------------ data - */

  function cached(handle) {
    if (swatchCache[handle] !== undefined) return swatchCache[handle];
    try {
      var v = sessionStorage.getItem('ss_cw_' + handle);
      if (v) return (swatchCache[handle] = JSON.parse(v));
    } catch (e) { /* private mode */ }
    return undefined;
  }

  function store(handle, value) {
    swatchCache[handle] = value;
    try { sessionStorage.setItem('ss_cw_' + handle, JSON.stringify(value)); } catch (e) {}
  }

  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* ---------------------------------------------------------------- render - */

  function build(card) {
    var handle = card.getAttribute('handle');
    var entry = byHandle[handle];
    if (!entry) return null;

    var row = entry.row;
    if (row.n < 2) return null;   /* one cover only — a strip of one says nothing */

    /* Lead with the colourway this card is actually showing */
    var here = entry.cover;
    var ordered = [here].concat(row.c.filter(function (c) { return c[1] !== here[1]; }));

    /* Mobile borrows the PDP's "Compare leather types" treatment: overlapping
       circles and a remainder, rather than a row of squares.

       How many circles fit depends on the card, not the breakpoint — the grid
       spans the first and last card across both columns, so those are roughly
       twice as wide. Measuring is more reliable than guessing from :first-child,
       because it holds however the grid is rearranged later. */
    var narrow = NARROW.matches;

    /* Relative, not absolute: a "wide" card is one spanning more than half the
       grid. Pixel thresholds do not survive a change of viewport — at 2000px a
       three-across card is already 660px wide, so every card looked "big". */
    var list = card.closest('product-list') || card.parentElement;
    var listWidth = list ? list.getBoundingClientRect().width : 0;
    var cardWidth = card.getBoundingClientRect().width;
    var wide = listWidth > 0 && cardWidth > listWidth * 0.55;

    var max;
    if (narrow) max = wide ? 6 : 3;
    else max = wide ? 9 : 5;

    var html = ordered.slice(0, max).map(function (c) {
      return '<a class="ss__sw' + (c[1] === here[1] ? ' is-match' : '') + '"' +
        ' href="/products/' + encodeURIComponent(c[1]) + '"' +
        ' data-handle="' + esc(c[1]) + '"' +
        ' data-name="' + esc(c[0]) + '"' +
        ' data-price="' + c[2] + '"' +
        ' title="' + esc(c[0]) + '"><span></span></a>';
    }).join('');

    /* Only label when covers are hidden. If every cover is already on screen
       there is nothing more to announce. */
    var more = row.n - Math.min(max, ordered.length);
    if (more > 0) {
      html += '<a class="ss__count" href="/products/' + encodeURIComponent(here[1]) + '"' +
              ' title="See all ' + row.n + ' colours">+' + more + ' colours</a>';
    }

    var wrap = document.createElement('span');
    wrap.className = 'ss__swatches ss__swatches--card ss__swatches--pill' + (wide ? ' ss__swatches--wide' : '');
    wrap.innerHTML = html;

    /* Overlaid on the bottom-left of the image, both breakpoints. */
    var host = card.querySelector('.product-card__figure')
            || card.querySelector('.product-card__media')
            || card.querySelector('.product-card__info')
            || card;
    host.appendChild(wrap);
    return ordered.slice(0, max);
  }

  /* Fill in the fabric photos for everything now on screen, in one request. */
  var CHUNK = 24;   /* Storefront API rejects very large aliased queries */

  function hydrate(handles) {
    var seen = {};
    var wanted = handles.filter(function (h) {
      if (seen[h] || cached(h) !== undefined) return false;
      return (seen[h] = true);
    });
    paint();
    if (!wanted.length || !TOKEN || !SHOP) return;
    for (var i = 0; i < wanted.length; i += CHUNK) fetchChunk(wanted.slice(i, i + CHUNK));
  }

  function fetchChunk(wanted) {
    var parts = wanted.map(function (h, i) {
      return 'p' + i + ': product(handle: ' + JSON.stringify(h) + ') {' +
        ' sw: metafield(namespace: "custom", key: "product_details_image") {' +
        '   reference { __typename ... on MediaImage { image { url(transform: {maxWidth: 96, maxHeight: 96, crop: CENTER}) } } ... on GenericFile { url } }' +
        ' } }';
    });

    fetch('https://' + SHOP + '/api/2024-01/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': TOKEN },
      body: JSON.stringify({ query: '{' + parts.join(' ') + '}' }),
    })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        if (!json.data) return;
        wanted.forEach(function (h, i) {
          var p = json.data['p' + i];
          if (!p) return store(h, null);
          var ref = (p.sw && p.sw.reference) || {};
          store(h, { swatch: ref.__typename === 'MediaImage' ? ref.image.url : (ref.url || null) });
        });
        paint();
      })
      .catch(function (err) { console.warn('Superba card swatches: fetch failed', err); });
  }

  function paint() {
    document.querySelectorAll('.ss__swatches--card .ss__sw[data-handle]').forEach(function (el) {
      var data = cached(el.dataset.handle);
      if (!data) return;
      var box = el.firstElementChild;
      if (box && data.swatch) box.style.backgroundImage = 'url(' + data.swatch + ')';
    });
  }

  /* No hover preview here by design — the swatch's title attribute names the
     cover, and clicking goes to that colourway. That lets the query below skip
     featuredImage entirely, roughly halving what we ask Shopify for. */

  /* -------------------------------------------------------------- lifecycle */

  var done = new WeakSet();

  /* Rotating a phone crosses the breakpoint, and the strip would otherwise keep
     whichever count it was built with. */
  function rebuild() {
    document.querySelectorAll('.ss__swatches--card').forEach(function (el) { el.remove(); });
    done = new WeakSet();
    decorate();
  }
  if (NARROW.addEventListener) NARROW.addEventListener('change', rebuild);
  else if (NARROW.addListener) NARROW.addListener(rebuild);

  function decorate() {
    var handles = [];
    document.querySelectorAll('product-card[handle]').forEach(function (card) {
      if (done.has(card)) return;
      done.add(card);
      var list = build(card);
      if (list) list.forEach(function (c) { handles.push(c[1]); });
    });
    if (handles.length) hydrate(handles);
  }

  fetch(INDEX_URL)
    .then(function (r) { return r.json(); })
    .then(function (data) {
      data.forEach(function (row) {
        (row.c || []).forEach(function (c) { byHandle[c[1]] = { row: row, cover: c }; });
      });
      decorate();

      /* Collection pages paginate and filter without a reload, so watch for
         cards arriving rather than decorating once and stopping. */
      if ('MutationObserver' in window) {
        var pending = null;
        new MutationObserver(function () {
          clearTimeout(pending);
          pending = setTimeout(decorate, 60);
        }).observe(document.body, { childList: true, subtree: true });
      }
    })
    .catch(function (err) { console.warn('Superba card swatches: index failed', err); });
})();
